import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Logo } from "../../Assets.tsx";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

/* Colours: text #4e5356 | buttons #2596be | headings #4573a6
   Fonts  : Ubuntu Bold (headings) / Open Sans Regular (body)
   Motion : the bar drops from the top, links fall in one by one, dropdowns open
            downward with items cascading, the mobile menu drops from the top. */

const services = [
  { label: "Web Development", to: "/website-development-services" },
  { label: "Full Stack", to: "/fullstack-service" },
  { label: "Mobile App", to: "/Mobile-App-service" },
  { label: "Software Testing / QA", to: "/contact" },
  { label: "E-Commerce", to: "/E-Commerce-Service" },
  { label: "Web Design", to: "/WebDesign-service" },
  { label: "Digital Marketing", to: "/digital-Marketing" },
];

const hire = [
  { label: "Frontend", section: "Development" },
  { label: "Backend", section: "Development" },
  { label: "QA", section: "Design & Experience" },
  { label: "UI/UX", section: "Quality" },
  { label: "Others", section: "Support" },
];

const links = [
  { label: "Service", to: "/Service", menu: services },
  { label: "Staff Augmentation", to: "/staff-augmentation" },
  { label: "Hire Experts", menu: hire },
  { label: "Products", to: "/Products" },
  { label: "Portfolio", to: "/Portfolio" },
  { label: "About Us", to: "/aboutUs" },
  { label: "Blog", to: "/Blog-page" },
];

const STYLES = `
@import url("https://fonts.googleapis.com/css2?family=Open+Sans:wght@400&family=Ubuntu:wght@700&display=swap");
.nv{--text:#4e5356;--head:#4573a6;--btn:#2596be;--btn-dark:#1d7fa3;--line:#d3e4ed;--tint:#eef6fa;
  position:fixed;top:12px;left:20px;right:20px;max-width:1320px;margin:0 auto;height:78px;z-index:1200;
  display:flex;align-items:center;justify-content:space-between;padding:0 18px 0 24px;border-radius:60px;
  background:rgba(255,255,255,.84);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
  border:1px solid var(--line);box-shadow:0 14px 40px -22px rgba(69,115,166,.55);
  font-family:"Open Sans",sans-serif;font-weight:400;color:var(--text);
  animation:nvDrop .9s cubic-bezier(.2,.8,.2,1) both;transition:top .35s,height .35s,box-shadow .35s}
.nv.scrolled{top:8px;height:64px;box-shadow:0 18px 44px -18px rgba(69,115,166,.7)}
.nv *{box-sizing:border-box}
.nv button{font-family:"Open Sans",sans-serif;font-weight:400;border:0;background:none;cursor:pointer;color:inherit}
.nv-logo{height:54px;cursor:pointer;display:block;object-fit:contain;transition:height .35s}
.nv.scrolled .nv-logo{height:44px}

.nv-links{display:flex;align-items:center;gap:2px}
.nv-item{position:relative}
.nv-link{display:inline-flex;align-items:center;gap:6px;padding:9px 15px;border-radius:50px;font-size:15px;color:var(--text);position:relative;
  background:linear-gradient(var(--tint),var(--tint)) no-repeat top/100% 0;transition:background-size .35s ease,color .25s}
.nv-item:hover>.nv-link{background-size:100% 100%;color:var(--btn)}
.nv-link.active{color:var(--head)}
.nv-link.active::after{content:"";position:absolute;left:50%;bottom:2px;width:5px;height:5px;margin-left:-2.5px;border-radius:50%;background:var(--btn)}
.nv-caret{width:6px;height:6px;border-right:2px solid currentColor;border-bottom:2px solid currentColor;transform:rotate(45deg) translateY(-2px);transition:transform .3s}
.nv-item:hover .nv-caret{transform:rotate(225deg) translate(-1px,-1px)}

/* dropdown: opens downward, items cascade */
.nv-menu{position:absolute;top:100%;left:50%;margin-left:-125px;width:250px;padding-top:16px;visibility:hidden;opacity:0;pointer-events:none;
  transform:translateY(-10px);transition:opacity .25s,transform .3s,visibility .3s}
.nv-item:hover .nv-menu,.nv-item:focus-within .nv-menu{visibility:visible;opacity:1;pointer-events:auto;transform:none}
.nv-card{background:#fff;border:1px solid var(--line);border-radius:18px;padding:8px;box-shadow:0 28px 50px -22px rgba(69,115,166,.55);
  clip-path:inset(0 0 100% 0 round 18px);transition:clip-path .45s cubic-bezier(.2,.8,.2,1)}
.nv-item:hover .nv-card,.nv-item:focus-within .nv-card{clip-path:inset(0 0 0 0 round 18px)}
.nv-card button{display:block;width:100%;text-align:left;padding:10px 14px;border-radius:12px;font-size:14px;color:var(--text);
  transition:background .2s,color .2s,padding-left .25s}
.nv-card button:hover{background:var(--tint);color:var(--btn);padding-left:22px}
.nv-item:hover .nv-card button{animation:itemDrop .45s cubic-bezier(.2,.8,.2,1) both}
${Array.from({ length: 8 }, (_, i) => `.nv-card button:nth-child(${i + 1}){animation-delay:${60 + i * 45}ms}`).join("")}

/* contact button: fill slides down */
.nv-cta{margin-left:10px;padding:12px 28px;border-radius:50px;font-size:15px;color:#fff!important;
  background:linear-gradient(var(--btn-dark),var(--btn-dark)) no-repeat top/100% 0,var(--btn);transition:background-size .4s ease,box-shadow .3s,transform .3s}
.nv-cta:hover{background-size:100% 100%,auto;box-shadow:0 14px 28px -10px rgba(37,150,190,.7);transform:translateY(2px)}

/* scroll progress along the bottom edge */
.nv-bar{position:absolute;left:34px;right:34px;bottom:0;height:3px;border-radius:3px;overflow:hidden;pointer-events:none}
.nv-bar i{display:block;height:100%;transform:scaleX(0);transform-origin:left;background:linear-gradient(90deg,var(--btn),var(--head))}

.nv-burger{display:none;align-items:center;justify-content:center;width:46px;height:46px;border-radius:50%;background:var(--tint)!important;color:var(--head)!important}

/* mobile drawer drops from the top */
.nv-drawer{position:fixed;inset:0;z-index:2000;background:#fff;overflow-y:auto;padding:18px 22px 40px;font-family:"Open Sans",sans-serif;
  color:#4e5356;animation:drawerDrop .55s cubic-bezier(.2,.8,.2,1) both}
.nv-drawer button{font-family:"Open Sans",sans-serif;font-weight:400;border:0;background:none;cursor:pointer;color:#4e5356;text-align:left}
.nv-dhead{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px}
.nv-close{width:46px;height:46px;border-radius:50%;background:#eef6fa!important;color:#4573a6!important;display:flex;align-items:center;justify-content:center}
.nv-row{width:100%;display:flex;align-items:center;justify-content:space-between;padding:15px 4px;font-size:18px;border-bottom:1px solid #d3e4ed;
  animation:itemDrop .5s cubic-bezier(.2,.8,.2,1) both}
.nv-row.active{color:#4573a6}
${Array.from({ length: 9 }, (_, i) => `.nv-drawer .nv-row:nth-of-type(${i + 1}){animation-delay:${150 + i * 55}ms}`).join("")}
.nv-sub{max-height:0;overflow:hidden;transition:max-height .5s ease}
.nv-sub.open{max-height:520px}
.nv-sub button{display:block;width:100%;padding:11px 18px;font-size:16px;border-radius:10px}
.nv-sub button:hover{background:#eef6fa;color:#2596be}
.nv-drawer .nv-cta{display:block;width:100%;margin:26px 0 0;text-align:center;padding:15px}

@keyframes nvDrop{from{transform:translateY(-160%);opacity:0}to{transform:none;opacity:1}}
@keyframes itemDrop{from{opacity:0;transform:translateY(-14px)}to{opacity:1;transform:none}}
@keyframes drawerDrop{from{transform:translateY(-100%)}to{transform:none}}

@media(max-width:1180px){.nv-links,.nv>.nv-cta{display:none}.nv-burger{display:flex}.nv{height:68px;left:12px;right:12px;padding:0 14px 0 18px}.nv-logo{height:46px}}
@media(prefers-reduced-motion:reduce){.nv,.nv *,.nv-drawer,.nv-drawer *{animation:none!important;transition:none!important}}
`;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const bar = useRef(null);
  const history = useHistory();
  const path = history.location.pathname;

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (bar.current) bar.current.style.transform = "scaleX(" + (max > 0 ? y / max : 0) + ")";
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useGSAP(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.from(".nv-item", { y: -30, opacity: 0, duration: 0.7, stagger: 0.08, delay: 0.5, ease: "power3.out", clearProps: "transform,opacity" });
  });

  const go = (item) => {
    setOpen(false);
    if (item.section) {
      history.push({ pathname: "/contact", state: { section: item.section } });
    } else {
      history.push(item.to);
    }
  };
  const isActive = (l) => l.to === path || (l.menu && l.menu.some((m) => m.to === path));

  return (
    <>
      <style>{STYLES}</style>
      <header className={"nv" + (scrolled ? " scrolled" : "")}>
        <img className="nv-logo" src={Logo} alt="ATUM" onClick={() => history.push("/")} />

        <nav className="nv-links" aria-label="Main">
          {links.map((l) => (
            <div className="nv-item" key={l.label}>
              <button
                type="button"
                className={"nv-link" + (isActive(l) ? " active" : "")}
                onClick={() => l.to && history.push(l.to)}
              >
                {l.label}
                {l.menu && <span className="nv-caret" />}
              </button>
              {l.menu && (
                <div className="nv-menu">
                  <div className="nv-card">
                    {l.menu.map((m) => (
                      <button type="button" key={m.label} onClick={() => go(m)}>{m.label}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <button type="button" className="nv-cta" onClick={() => history.push("/contact")}>Contact Us</button>

        <button type="button" className="nv-burger" onClick={() => setOpen(true)} aria-label="Open menu">
          <MenuIcon style={{ fontSize: 28 }} />
        </button>

        <div className="nv-bar"><i ref={bar} /></div>
      </header>

      {open && (
        <div className="nv-drawer">
          <div className="nv-dhead">
            <img className="nv-logo" src={Logo} alt="ATUM" onClick={() => go({ to: "/" })} />
            <button type="button" className="nv-close" onClick={() => setOpen(false)} aria-label="Close menu">
              <CloseIcon />
            </button>
          </div>

          {links.map((l) =>
            l.menu ? (
              <React.Fragment key={l.label}>
                <button
                  type="button"
                  className={"nv-row" + (isActive(l) ? " active" : "")}
                  onClick={() => setOpenSection(openSection === l.label ? null : l.label)}
                >
                  {l.label}
                  <span className="nv-caret" style={{ transform: openSection === l.label ? "rotate(225deg)" : "rotate(45deg)" }} />
                </button>
                <div className={"nv-sub" + (openSection === l.label ? " open" : "")}>
                  {l.menu.map((m) => (
                    <button type="button" key={m.label} onClick={() => go(m)}>{m.label}</button>
                  ))}
                </div>
              </React.Fragment>
            ) : (
              <button type="button" key={l.label} className={"nv-row" + (isActive(l) ? " active" : "")} onClick={() => go(l)}>
                {l.label}
              </button>
            )
          )}

          <button type="button" className="nv-cta" onClick={() => go({ to: "/contact" })}>Contact Us</button>
        </div>
      )}
    </>
  );
};

export default Navbar;